import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { API, pageVariants } from './utils';
import { stopAllAudio } from './voiceUtils';
import { useGuestPortal } from './GuestPortalContext';

export function BitsyCheckoutView({ setView, setSuccessMessage, onSwitchToForm }) {
  const { t, language } = useGuestPortal();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [roomNumber, setRoomNumber] = useState(null);
  const [employeeName, setEmployeeName] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [autoMicEnabled, setAutoMicEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  useEffect(() => {
    startConversation();
  }, []);
  
  const startConversation = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chatbot/checkout`, {
        message: "start",
        session_id: null
      });
      setSessionId(response.data.session_id);
      setMessages([{ role: "assistant", content: response.data.response }]);
      playVoiceMessage(response.data.response);
    } catch (error) {
      console.error("Error starting Bitsy checkout:", error);
      setMessages([{ 
        role: "assistant", 
        content: language === 'es' 
          ? "Lo siento, estoy teniendo problemas. Por favor usa el formulario."
          : "I'm sorry, I'm having trouble. Please use the form."
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };
  
  // Local playVoiceMessage for Bitsy chat (generates TTS from text)
  const playVoiceMessage = async (text, shouldAutoRecord = true) => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.onended = null;
      currentAudioRef.current.onerror = null;
      if (currentAudioRef.current.src) {
        URL.revokeObjectURL(currentAudioRef.current.src);
      }
      currentAudioRef.current = null;
    }
    
    setIsPlaying(true);
    
    try {
      const voiceResponse = await axios.get(`${API}/voice-dynamic/greeting/guest?lang=${language}&custom_text=${encodeURIComponent(text)}`, {
        responseType: 'blob'
      });
      const audioUrl = URL.createObjectURL(voiceResponse.data);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        if (autoMicEnabled && shouldAutoRecord) {
          setTimeout(() => {
            if (!isRecording && !isLoading) startRecording();
          }, 500);
        }
      };
      
      audio.onerror = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
      };
      
      audio.play().catch(e => {
        console.log("Audio play failed:", e);
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        if (autoMicEnabled && shouldAutoRecord) {
          setTimeout(() => startRecording(), 500);
        }
      });
    } catch (error) {
      console.log("Voice not available:", error);
      setIsPlaying(false);
      if (autoMicEnabled && shouldAutoRecord) {
        setTimeout(() => startRecording(), 500);
      }
    }
  };
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      toast.info(language === 'es' ? "Escuchando..." : "Listening...", { duration: 2000 });
    } catch (error) {
      console.error("Microphone error:", error);
      toast.error(language === 'es' ? "No se pudo acceder al micrófono" : "Could not access microphone");
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };
  
  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      const response = await axios.post(`${API}/chatbot/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const transcript = response.data.transcript || response.data.text || "";
      if (transcript.trim()) {
        const command = detectVoiceCommand(transcript.trim().toLowerCase());
        if (command) {
          setMessages(prev => [...prev, { role: "user", content: transcript }]);
          setInputValue("");
          await executeVoiceCommand(command);
        } else {
          setInputValue("");
          sendMessageWithText(transcript);
        }
      } else {
        toast.error(language === 'es' ? "No escuché nada." : "Didn't catch that.");
        setIsLoading(false);
      }
    } catch (error) {
      toast.error(language === 'es' ? "Error al transcribir" : "Transcription failed");
      setIsLoading(false);
    }
  };
  
  const detectVoiceCommand = (text) => {
    const commands = {
      cancel: ['cancel', 'cancelar', 'go back', 'volver', 'exit', 'salir'],
      restart: ['start over', 'restart', 'empezar de nuevo', 'reset'],
      help: ['help', 'ayuda', 'commands', 'comandos']
    };
    for (const [command, triggers] of Object.entries(commands)) {
      for (const trigger of triggers) {
        if (text.includes(trigger)) return command;
      }
    }
    return null;
  };
  
  const executeVoiceCommand = async (command) => {
    let response = "";
    switch (command) {
      case 'cancel':
        response = language === 'es' ? "Volviendo al menú..." : "Going back to menu...";
        setMessages(prev => [...prev, { role: "assistant", content: response }]);
        playVoiceMessage(response, false);
        setTimeout(() => setView("menu"), 2000);
        break;
      case 'restart':
        response = language === 'es' ? "Empecemos de nuevo. ¿Cuál es tu número de habitación?" : "Let's start over. What's your room number?";
        setMessages([{ role: "assistant", content: response }]);
        setBookingId(null);
        setRoomNumber(null);
        setEmployeeName(null);
        playVoiceMessage(response);
        break;
      case 'help':
        response = language === 'es' 
          ? "Di 'Cancelar' para volver, 'Empezar de nuevo' para reiniciar, o tu número de habitación."
          : "Say 'Cancel' to go back, 'Start over' to restart, or your room number.";
        setMessages(prev => [...prev, { role: "assistant", content: response }]);
        playVoiceMessage(response);
        break;
      default:
        break;
    }
    setIsLoading(false);
  };
  
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    sendMessageWithText(userMessage, false);
  };
  
  const sendMessageWithText = async (text, addUserMessage = true) => {
    if (addUserMessage) {
      setMessages(prev => [...prev, { role: "user", content: text }]);
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chatbot/checkout`, {
        message: text,
        session_id: sessionId
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      if (response.data.booking_id) setBookingId(response.data.booking_id);
      if (response.data.room_number) setRoomNumber(response.data.room_number);
      if (response.data.employee_name) setEmployeeName(response.data.employee_name);
      
      if (response.data.checkout_complete) {
        setTimeout(() => {
          if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            if (currentAudioRef.current.src) URL.revokeObjectURL(currentAudioRef.current.src);
            currentAudioRef.current = null;
          }
          stopAllAudio();
          setSuccessMessage({
            title: language === 'es' ? "¡Salida Completa!" : "Check-Out Complete!",
            message: language === 'es' 
              ? `¡Gracias por hospedarse, ${response.data.employee_name || employeeName || 'Invitado'}!`
              : `Thanks for staying, ${response.data.employee_name || employeeName || 'Guest'}!`,
            subMessage: language === 'es'
              ? "No olvide dejar su llave en la Caja de Llaves"
              : "Don't forget to drop your key in the Key Drop Box"
          });
          setView("checkout-success");
        }, 1500);
      }
      playVoiceMessage(response.data.response);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: language === 'es' ? "Lo siento, intenta de nuevo." : "Sorry, try again."
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-lg"
    >
      <Card className="bg-vault-surface border border-vault-border shadow-xl rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-white/20 rounded-full flex items-center justify-center ${isPlaying ? 'animate-pulse' : ''}`}>
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Bitsy</h2>
                <p className="text-white/70 text-sm">
                  {isPlaying ? (language === 'es' ? 'Hablando...' : 'Speaking...') 
                    : isRecording ? (language === 'es' ? 'Escuchando...' : 'Listening...')
                    : (language === 'es' ? 'Asistente de Salida' : 'Check-Out Assistant')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAutoMicEnabled(!autoMicEnabled)}
                className={`p-2 rounded-full ${autoMicEnabled ? 'bg-white/30' : 'bg-white/10'}`}
              >
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
              <Button onClick={() => setView("menu")} variant="ghost" className="text-white hover:bg-white/20" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t.back || "Back"}
              </Button>
            </div>
          </div>
          <div className="mt-2 text-center">
            <span className={`text-xs px-2 py-1 rounded-full ${autoMicEnabled ? 'bg-green-500/30 text-green-200' : 'bg-gray-500/30 text-gray-300'}`}>
              {autoMicEnabled ? '🎤 Hands-free ON' : '🎤 Hands-free OFF'}
            </span>
          </div>
        </div>
        
        {/* Messages */}
        <div className="h-64 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-2xl ${
                msg.role === 'user' ? 'bg-orange-500 text-white rounded-br-md' : 'bg-white text-gray-800 border rounded-bl-md shadow-sm'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white p-3 rounded-2xl rounded-bl-md border">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isLoading && !isRecording}
              className={`${isRecording ? 'bg-red-500 animate-pulse' : 'bg-orange-500 hover:bg-orange-600'} text-white`}
            >
              {isRecording ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              )}
            </Button>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isRecording ? "Listening..." : "Type or speak..."}
              className="flex-1"
              disabled={isLoading || isRecording}
            />
            <Button onClick={sendMessage} disabled={isLoading || !inputValue.trim()} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          {isRecording && (
            <div className="flex items-center justify-center gap-2 mt-2 text-red-500">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-sm">{language === 'es' ? "Grabando..." : "Recording..."}</span>
            </div>
          )}
          <button onClick={onSwitchToForm} className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 underline">
            {language === 'es' ? 'O usa el formulario' : 'Or use form'}
          </button>
        </div>
      </Card>
    </motion.div>
  );
}
